import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, TextButton, Text } from '@oneaudi/unified-web-components';
import { ACTIONS, SELECTORS } from '@oneaudi/stck-store';
import { useI18n } from '@oneaudi/i18n-context';
import {
  VehicleBasic,
  getRelatedProduct,
  useServicesContext,
  dynamicStylingHelper,
} from '@oneaudi/vtp-shared';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { getAppContext } from '../../hooks/tileHook';
import {
  trackAddToFav,
  trackDetailsPageNav,
  trackRemoveFromFav,
} from '../../tracking/tilesTracking';
import {
  ButtonContainer,
  FavoritesContainer,
  FavoritesOverlay,
  GridButtonContainer,
} from './ActionButtons.styles';
import { useVehicleDetailsPageUrl } from '../../hooks/useVehicleDetailsPageUrl';

interface Props {
  vehicle: VehicleBasic;
}

const ActionButtons: React.FC<Props> = ({ vehicle }) => {
  const detailsPageLink = useVehicleDetailsPageUrl(vehicle) || '';
  const goToDetailsLabel = useI18n({
    id: 'nemo.ui.sc.result.details.button',
    defaultMessage: 'Go to Details',
  });
  const addFavoriteLabel = useI18n({ id: 'nemo.ui.sc.result.addfavorite', defaultMessage: '' });
  const favoriteAreaLabelNotSaved = useI18n({
    id: 'stockcars.button.favourites.is-not-saved.arialabel',
    defaultMessage: '',
  });
  const favoriteAreaLabelSaved = useI18n({
    id: 'stockcars.button.favourites.is-saved.arialabel',
    defaultMessage: '',
  });
  const removeFavoriteLabel = useI18n({
    id: 'nemo.ui.sc.result.removefavorite',
    defaultMessage: '',
  });
  const favoritesLayerHeadline = useI18n({
    id: 'nemo.ui.sc.favorites.layer.headline',
    defaultMessage: '',
  });
  const favoritesLayerCopy = useI18n({ id: 'nemo.ui.sc.favorites.layer.copy', defaultMessage: '' });
  const favoritesLayerYes = useI18n({
    id: 'nemo.ui.sc.favorites.layer.button.yes',
    defaultMessage: 'Yes',
  });
  const favoritesLayerNo = useI18n({
    id: 'nemo.ui.sc.favorites.layer.button.no',
    defaultMessage: 'No',
  });

  const appContext = getAppContext();
  const dispatch = useDispatch();
  const isFavorite = useSelector((state: {}) =>
    SELECTORS.VEHICLES.isFavoriteVehicle(state, vehicle.id)
  );
  const [showFavoritesLayer, setShowFavoritesLayer] = useState(false);

  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;

  const relatedProduct = getRelatedProduct(vehicle);

  const handleFavoritesClick = () => {
    if (isFavorite) {
      if (appContext !== 'results') {
        setShowFavoritesLayer(true);
      } else {
        removeFavorites();
      }
    } else {
      dispatch(
        ACTIONS.FAVORITE_VEHICLES.addFavoriteVehicleId({
          vehicleId: vehicle.id,
        })
      );
      trackAddToFav(trackingService, addFavoriteLabel, relatedProduct);
    }
  };
  const removeFavorites = () => {
    dispatch(
      ACTIONS.FAVORITE_VEHICLES.removeFavoriteVehicleId({
        vehicleId: vehicle.id,
      })
    );
    setShowFavoritesLayer(false);
    trackRemoveFromFav(trackingService, removeFavoriteLabel, relatedProduct);
  };

  return (
    <>
      <ButtonContainer>
        <TextButton
          size={dynamicStylingHelper(undefined, 'sm')}
          variant="primary"
          data-testid="detailsBtn"
          href={detailsPageLink}
          isFullWidth
          onClick={() =>
            trackDetailsPageNav(
              trackingService,
              `${vehicle.symbolicCarline?.description}`,
              detailsPageLink,
              'button'
            )
          }
        >
          {goToDetailsLabel}
        </TextButton>
        <FavoritesContainer>
          <IconButton
            variant="secondary"
            icon={isFavorite ? 'favorite-active' : 'favorite'}
            size={dynamicStylingHelper('md', 'sm')}
            type="button"
            onClick={handleFavoritesClick}
            data-testid="favoritesBtn"
            aria-label={isFavorite ? favoriteAreaLabelSaved : favoriteAreaLabelNotSaved}
          />
        </FavoritesContainer>
      </ButtonContainer>
      {showFavoritesLayer && (
        <FavoritesOverlay>
          <Text variant={{ order: '3', style: 'bold', type: 'copy' }}>
            {favoritesLayerHeadline}
          </Text>
          <Text variant={{ order: '2', style: 'normal', type: 'copy' }}>{favoritesLayerCopy}</Text>
          <GridButtonContainer data-testid="removeFavoriteLayer">
            <TextButton variant="primary" onClick={removeFavorites} data-testid="removeFavorite">
              {favoritesLayerYes}
            </TextButton>
            <TextButton
              variant="secondary"
              onClick={() => setShowFavoritesLayer(false)}
              data-testid="closeRemoveFavoriteLayer"
            >
              {favoritesLayerNo}
            </TextButton>
          </GridButtonContainer>
        </FavoritesOverlay>
      )}
    </>
  );
};

export default ActionButtons;
